import type { ClassCancelPath } from "#/src/pages/_components/CancelSessionConfirmationModal/classCancelPaths";
import type { Rating } from "#/src/pages/_components/FiveStars/FiveStarsInteractive";
import type { SessionReviewEntrypoint } from "#/src/pages/_components/ReviewSessionModal/ReviewSession";
import { SubstituteBookingSuggestionModalProps } from "#/src/pages/_components/SubstituteBookingSuggestionModal";
import type { ParentReferralLinkUtmParams } from "#/src/utils/parentReferral";
import { MarketplaceSessionAvailablePayload } from "@web-monorepo/api-types/zodSchemas/realtime/marketplace";
import { proxy, useSnapshot } from "@web-monorepo/hooks";
import type { CourseWaitlistDetails } from "#/src/pages/_components/CourseWaitlistModal/types";

export type PriorityModalConfiguration = {
  type: "lastMinuteSessionOfferModal";
  offer: MarketplaceSessionAvailablePayload;
  onTimeout: () => void;
  onReject: () => void;
};

export type ParentSubjectGradeModalSelection = {
  grade?: string;
  subject?: string;
};

export type SubscriptionCrossSellContext = {
  tutorId?: string;
  subject?: string;
  grade?: string;
};

export type ParentSubjectGradeModalModeConfig =
  | {
      kind?: "tutor-search";
    }
  | {
      kind: "groupclass-search";
      initialGrade?: string;
      initialSubject?: string;
      onSubmit?: (selection: ParentSubjectGradeModalSelection) => void;
      onSkip?: () => void;
    };

export type ModalConfiguration =
  | {
      type: "cancel";
      enrollmentId: string;
      fromReschedule?: boolean;
    }
  | {
      type: "subscribe";
    }
  | {
      type: "teacher";
      teacherUID: string;
      options?: {
        onSelectTutor: () => void;
        backButtonText: string;
      };
    }
  | {
      type: "classDiscountExclusion";
    }
  | {
      type: "tutorUnavailable";
      tutorId: string;
    }
  | {
      type: "cancelSubscriptionInterventionModal";
      subscriptionId: number;
      isGroupSubscription: boolean;
    }
  | {
      type: "cancelSessionConfirmationModal";
      studentSessionId: string;
      isEligibleForRefund: boolean;
      isSubstituteSession: boolean;
      cancelByDate: Date;
      onGoBack?: () => void;
      onSuccess?: () => void;
      successEvents?: Array<{ eventName: string; metadata?: Record<string, unknown> }>;
      cancellationPath?: ClassCancelPath;
    }
  | {
      type: "cancelSessionListModal";
      subscriptionId: number;
    }
  | {
      type: "videoRatingModal";
      sessionId: string;
    }
  | {
      type: "freeClassesPopup";
    }
  | {
      type: "courseWaitlist";
      preferredTimeRange: {
        startTime: Date;
        endTime: Date;
      };
      details: CourseWaitlistDetails;
    }
  | {
      type: "sessionReview";
      sessionId: string;
      initialStarRating: Rating;
      entrypoint: SessionReviewEntrypoint;
    }
  | {
      type: "tutorClipsAnnouncementModal";
    }
  | {
      type: "parentSubjectGradeModal";
      tutorUID?: string;
      submitButtonText?: string;
      selectionMode?: "grade-and-subject" | "grade-only";
      titleMode?: "search" | "edit";
      startGrade?: string;
      endGrade?: string;
      modeConfig?: ParentSubjectGradeModalModeConfig;
    }
  | {
      type: "searchFiltersModal";
    }
  | {
      type: "selectDateTimeModal";
      tutorUID: string;
      value?: Date;
      submitButtonText?: string;
      onContinue: (selectedDateTime: Date, tutorUID: string) => void;
      filteredDays?: string[];
      filteredTimeRange?: string;
    }
  | {
      type: "specializationSelectionModal";
      tutorUID: string;
    }
  | {
      type: "addChildModal";
      value?: string;
      existingChildren?: readonly string[];
      onSubmit: (childName: string) => void;
    }
  | ({ type: "substituteBookingSuggestion" } & SubstituteBookingSuggestionModalProps)
  | {
      type: "confirmWeeklyClassesModal";
      tutorId: string;
      onBookWeeklyClasses: () => void;
      onNotRightNow: () => void;
    }
  | {
      type: "sessionInProgress";
      onJoinClass: () => void;
      isGroupSession: boolean;
    }
  | {
      type: "tutorPricingModal";
      tutorId: string;
      tutorName: string;
      userHadSessionWithTutor: boolean;
      isExperienced: boolean;
      isFiftyMinSessionAllowed: boolean;
    }
  | {
      type: "discountEligibility";
    }
  | {
      type: "referralReward";
      referralLinkUtm: ParentReferralLinkUtmParams;
      /** When set, the modal invites a friend to this group class via the course-PDP referral link. */
      groupClassCourseId?: string;
      subscriptionContext?: SubscriptionCrossSellContext;
    };

export const modalStore = proxy<{
  modal?: ModalConfiguration;
  modalQueue: ModalConfiguration[];
  priorityModal?: PriorityModalConfiguration;
}>({
  modalQueue: [],
});

function advanceModalQueue() {
  const [nextModal, ...remainingQueue] = modalStore.modalQueue;
  modalStore.modalQueue = remainingQueue;
  if (nextModal) {
    modalStore.modal = nextModal;
  } else {
    delete modalStore.modal;
  }
}

// Closing a modal advances the standard modal queue so interruptive surfaces stay serialized.
export const hideModal = () => {
  advanceModalQueue();
};
export const showModal = (modal: ModalConfiguration) => (modalStore.modal = modal);
export const queueModal = (modal: ModalConfiguration) => {
  modalStore.modalQueue = [...modalStore.modalQueue, modal];
  if (modalStore.modal == null) {
    advanceModalQueue();
  }
};
export const useIsModalVisible = () => {
  const { modal } = useSnapshot(modalStore);
  return modal != null;
};

export const showPriorityModal = (modal: PriorityModalConfiguration) => (modalStore.priorityModal = modal);
export const hidePriorityModal = () => delete modalStore.priorityModal;
export const useIsPriorityModalVisible = () => {
  const { priorityModal } = useSnapshot(modalStore);
  return priorityModal != null;
};

export const useModalStoreSnapshot = () => useSnapshot(modalStore);
